import React, { FC, useEffect, useState } from "react";
import { Download, FileText, ImageIcon, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { whatsappService } from "@/services/whatsapp-service";

type WhatsAppMediaKind = "image" | "document";

interface WhatsAppMediaAttachmentProps {
  hotelId?: string;
  externalChannel?: string;
  mediaKind?: WhatsAppMediaKind;
  mediaId?: string;
  mimeType?: string;
  caption?: string;
  filename?: string;
  whatsappMessageId?: string;
  whatsappConversationId?: string;
}

const MIME_EXTENSION: Record<string, string> = {
  "application/pdf": "pdf",
  "image/gif": "gif",
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
};

const safeFilename = (filename: string): string =>
  filename.replace(/[\\/:*?"<>|]+/g, "-").trim();

const fallbackFilename = ({
  mediaKind,
  mediaId,
  mimeType,
}: {
  mediaKind: WhatsAppMediaKind;
  mediaId: string;
  mimeType?: string;
}): string => {
  const extension = mimeType ? MIME_EXTENSION[mimeType] : undefined;
  const basename =
    mediaKind === "image"
      ? `whatsapp-image-${mediaId}`
      : `whatsapp-document-${mediaId}`;

  return extension ? `${basename}.${extension}` : basename;
};

const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const openBlob = (blob: Blob, filename: string): boolean => {
  const url = URL.createObjectURL(blob);
  const opened = window.open(url, "_blank", "noopener,noreferrer");

  if (!opened) {
    URL.revokeObjectURL(url);
    downloadBlob(blob, filename);
    return false;
  }

  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return true;
};

export const WhatsAppMediaAttachment: FC<WhatsAppMediaAttachmentProps> = ({
  hotelId,
  externalChannel,
  mediaKind,
  mediaId,
  mimeType,
  caption,
  filename,
  whatsappMessageId,
  whatsappConversationId,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  if (
    externalChannel !== "WHATSAPP" ||
    !hotelId ||
    !mediaKind ||
    !mediaId ||
    !whatsappMessageId ||
    !whatsappConversationId
  ) {
    return null;
  }

  const fallback = fallbackFilename({ mediaKind, mediaId, mimeType });
  const resolvedFilename = safeFilename(filename || fallback) || fallback;
  const shouldOpen = mediaKind === "image" || mimeType === "application/pdf";
  const Icon = mediaKind === "image" ? ImageIcon : FileText;
  const actionLabel = shouldOpen
    ? t("chats.openWhatsappMedia")
    : t("chats.downloadWhatsappMedia");

  let actionIcon = <Download className="size-3" />;
  if (isLoading) {
    actionIcon = <Loader2 className="size-3 animate-spin" />;
  } else if (shouldOpen) {
    actionIcon = <Icon className="size-3" />;
  }

  const handleOpen = async () => {
    try {
      setIsLoading(true);
      const blob = await whatsappService.downloadMessageMedia(
        hotelId,
        whatsappConversationId,
        whatsappMessageId,
      );

      if (mediaKind === "image") {
        setImagePreviewUrl(URL.createObjectURL(blob));
      } else if (shouldOpen) {
        openBlob(blob, resolvedFilename);
      } else {
        downloadBlob(blob, resolvedFilename);
      }
    } catch {
      toast.error(t("chats.whatsappMediaError"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="mt-1 flex max-w-full flex-col gap-1 rounded-md border border-slate-200 bg-white/60 p-2">
        <div className="flex min-w-0 items-center gap-2">
          <Icon className="size-4 shrink-0 text-slate-500" aria-hidden />
          <span className="truncate text-xs font-medium text-slate-700">
            {filename || t("chats.whatsappMedia")}
          </span>
        </div>
        {caption && (
          <p className="line-clamp-2 text-xs break-words text-slate-500">
            {caption}
          </p>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 w-fit px-2 text-[11px]"
          onClick={handleOpen}
          disabled={isLoading}
        >
          {actionIcon}
          {actionLabel}
        </Button>
      </div>

      <Dialog
        open={Boolean(imagePreviewUrl)}
        onOpenChange={(open) => {
          if (!open) {
            setImagePreviewUrl(null);
          }
        }}
      >
        <DialogContent className="flex max-h-[90vh] w-[min(1000px,calc(100vw-2rem))] max-w-none flex-col overflow-hidden p-4">
          <DialogHeader>
            <DialogTitle className="truncate pr-8">
              {filename || t("chats.whatsappMedia")}
            </DialogTitle>
          </DialogHeader>
          {imagePreviewUrl ? (
            <img
              src={imagePreviewUrl}
              alt={caption || filename || t("chats.whatsappMedia")}
              className="min-h-0 w-full object-contain"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};
